import { useEffect, useState } from "react";
import badgesBg from "assets/images/others/host-side-images/badgesBg.png";
import { siteRoutes } from "utils/helpers/enums/routes";
import MinorBreadcrumb, {
  MinorBreadcrumbLink,
} from "components/breadcrumbs/MinorBreadcrumb";
import { BAGDE_TYPES } from "utils/helpers/enums/shared.enum";
import useMinor from "../useHooks";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import { useSelector } from "react-redux";
import Pagination from "components/pagination";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import badgesNotFoundGif from "assets/images/gifs/badge-not-found.gif";

const DigitalPassport = () => {
  const [activeTab, setActiveTab] = useState("Learn Local's Badges");
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [filters, setFilters] = useState({
    page: 0,
    size: 10,
    totalElements: 0,
  });

  const breadcrumbLinks: MinorBreadcrumbLink[] = [
    { title: "Home", path: siteRoutes.minorHome },
    {
      title: "Digital Passport",
      path: siteRoutes.minorDigitalPassport,
    },
  ];

  const [data, setData] = useState<any>([]);

  // Fetching Badges
  const { getMyBadges } = useMinor();

  const loadBadges = (page = 0, size = 10) => {
    const queryParams: any = {
      page,
      size,
    };

    if (activeTab === "Learn Local's Badges") {
      queryParams.badgeType = BAGDE_TYPES.ACHIEVEMENT;
    } else if (activeTab === "Event Badges") {
      queryParams.badgeType = BAGDE_TYPES.EVENT;
    }

    getMyBadges(setData, queryParams, (totalElements: number) => {
      setFilters((prev) => ({
        ...prev,
        totalElements,
      }));
    });

    // Mark initial load as complete after first API call
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  };

  const handlePageChange = ({
    selected,
    limit,
  }: {
    selected: number;
    limit: number;
  }) => {
    setFilters({
      ...filters,
      page: selected,
      size: limit,
    });
    loadBadges(selected, limit);
  };

  useEffect(() => {
    // Reset pagination when tab changes
    setFilters({
      page: 0,
      size: 10,
      totalElements: 0,
    });
    loadBadges(0, 10);
  }, [activeTab]);

  // Mark initial load as complete when loading finishes
  useEffect(() => {
    if (isInitialLoad && !isLoading) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  return (
    <div className="container min-h-[70vh]">
      <MinorBreadcrumb links={breadcrumbLinks} />
      <h2 className="text-2xl font-bold mb-4 my-6">My Digital Passport</h2>

      <div className="">
        {/* Tab Navigation */}
        <div className="flex justify-start my-6">
          <button
            type="button"
            className={`px-4 py-2 text-sm font-semibold ${
              activeTab === "Learn Local's Badges"
                ? "text-minorPrimary border-b-2 border-minorPrimary"
                : "text-grayText hover:text-minorPrimary"
            }`}
            onClick={() => setActiveTab("Learn Local's Badges")}
          >
            Learn Local's Badges
          </button>
          <button
            type="button"
            className={`px-4 py-2 text-sm font-semibold ${
              activeTab === "Event Badges"
                ? "text-minorPrimary border-b-2 border-minorPrimary"
                : "text-grayText hover:text-minorPrimary"
            }`}
            onClick={() => setActiveTab("Event Badges")}
          >
            Event Badges
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === "Learn Local’s Badges" && (
            <div className="flex flex-wrap gap-4 py-5">
              {data?.map((badge: any) => (
                <div
                  key={badge.id}
                  className="flex flex-col items-center justify-center p-4 rounded-lg w-[235px] h-[260px]"
                  style={{ backgroundImage: `url(${badgesBg})` }}
                >
                  <div className="hover:scale-110 transition-transform duration-300">
                    <img
                      src={concatPathWithBackendUrl(badge?.image?.name)}
                      className="h-[190px]"
                      alt="badge"
                    />
                  </div>
                  <span className="text-sm font-medium mt-2 text-blackie">
                    {badge.name}
                  </span>
                </div>
              ))}
            </div>
          )}

          {activeTab === "Event Badges" && (
            <div className="flex flex-wrap gap-4 py-5">
              {data?.map((badge: any) => (
                <div
                  key={badge.id}
                  className="flex flex-col items-center justify-center p-4 rounded-lg w-[235px] h-[260px]"
                  style={{ backgroundImage: `url(${badgesBg})` }}
                >
                  <div className="hover:scale-110 transition-transform duration-300">
                    <img
                      src={concatPathWithBackendUrl(badge?.image?.name)}
                      alt="badge"
                      className="h-[190px]"
                    />
                  </div>
                  <span className="text-sm font-medium mt-2 text-blackie">
                    {badge.name}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex  justify-center">
          <CustomMessageDisplay
            show={!isLoading && !data.length && !isInitialLoad}
            title="No Badges Available"
            slogan={
              activeTab === "Learn Local's Badges"
                ? "You haven't earned any Learn Local badges yet. Keep participating in experiences to earn achievement badges!"
                : "You haven't earned any event badges yet. Join and complete events to earn event badges!"
            }
            image={badgesNotFoundGif}
            imageAlt="No badges found"
            className="py-12"
            imageClassName="w-[150px] h-[120px]"
            titleClassName="text-2xl font-bold text-blackie"
            sloganClassName="text-gray-500 text-base"
          />
        </div>
      </div>

      {data.length > 0 && filters.totalElements > filters.size && (
        <Pagination
          count={filters.totalElements}
          page={filters.page}
          limit={filters.size}
          onPageChange={handlePageChange}
          activeBgColor={"#DE9C27"}
          activeTextColor={"#ffffff"}
        />
      )}
    </div>
  );
};

export default DigitalPassport;
